import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';

export type DocKind = 'billing_statement' | 'inspection_report' | 'contract' | 'job_card_crew' | 'job_card_contractor'
    | 'job_card_treater' | 'work_completion' | 'combined' | 'other' | 'library';

export interface DmDocument { key: DocKind; label: string; file: string | null; url: string | null; envelope: string | null; }
export interface DmCombined { id: number; name: string; parts: string[]; file: string; url: string; created: string; envelope: string | null; }
export interface DmOtherFile { id: number; name: string; file: string; url: string; is_pdf: boolean; is_image: boolean; envelope: string | null; }
export interface DmLibraryFile { id: number; name: string; type: string; auto_attach: boolean; url: string; created: string; }
export interface DmParty { kind: 'client' | 'inspector'; id: number; role: string; name: string; email: string; }
export interface DmHistory { id: number; sent: string; type: string; sent_by: string; to_name: string; to_email: string; attachments: string[]; can_resend: boolean; }
export interface DmCan { combine: boolean; upload: boolean; delete: boolean; email: boolean; replies: boolean; report_sent: boolean; resend: boolean; library: boolean; docusign: boolean; }

export interface DmPage {
    job_id: number;
    documents: DmDocument[];
    combined: DmCombined[];
    other_files: DmOtherFile[];
    library: DmLibraryFile[];
    parties: DmParty[];
    replies: { id: number, name: string }[];
    report_sent: boolean;
    supplemental: boolean;
    history: DmHistory[];
    docusign_connected: boolean;
    upload_max_kb: number;
    can: DmCan;
}

export interface DmItem { kind: DocKind; id?: number; }

/** Document Manager API (twin-laravel DocumentManagerController; was twin-ci inspections_reports). */
@Injectable({ providedIn: 'root' })
export class DocumentManagerService {
    private base(jobId) { return environment.api + 'jobs/' + jobId + '/documents'; }

    constructor(private http: HttpClient) { }

    page(jobId): Observable<DmPage> {
        return this.http.get<DmPage>(this.base(jobId));
    }

    /** The file itself (signed-in download; the API checks the job and the role). */
    download(jobId, item: DmItem): Observable<Blob> {
        return this.http.get(this.base(jobId) + '/download/' + item.kind + (item.id ? '/' + item.id : ''), { responseType: 'blob' });
    }

    combine(jobId, items: DmItem[]): Observable<any> {
        return this.http.post(this.base(jobId) + '/combine', { items });
    }

    deleteCombined(jobId, id: number): Observable<any> {
        return this.http.delete(this.base(jobId) + '/combined/' + id);
    }

    upload(jobId, name: string, file: File): Observable<any> {
        const form = new FormData();
        form.append('name', name);
        form.append('file', file, file.name);
        return this.http.post(this.base(jobId) + '/other-files', form);
    }

    deleteOther(jobId, id: number): Observable<any> {
        return this.http.delete(this.base(jobId) + '/other-files/' + id);
    }

    email(jobId, body: { recipients: { kind: string, id: number }[], extra: string[], attachments: DmItem[], reply_id: number | null, text: string, send_original: boolean }): Observable<any> {
        return this.http.post(this.base(jobId) + '/email', body);
    }

    setReportSent(jobId, sent: boolean): Observable<any> {
        return this.http.put(this.base(jobId) + '/report-sent', { sent });
    }

    reply(jobId, id: number): Observable<any> {
        return this.http.get(this.base(jobId) + '/replies/' + id);
    }

    resend(jobId, historyId: number, email: string): Observable<any> {
        return this.http.post(this.base(jobId) + '/history/' + historyId + '/resend', { email });
    }

    docusign(jobId, kind: 'contract' | 'combined' | 'other', id?: number): Observable<any> {
        return this.http.post(this.base(jobId) + '/docusign', { kind, id: id || null });
    }
}
